import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import NoQrScanReason from './NoQrScanReason';
import ValveIdInput from './ValveIdInput';
import { useValveStates } from '../../hooks/useValveStates';
import { useCreateAdHocJob } from '../../hooks/useJobs';
import { useEffect } from 'react';
import { SiteContext } from '../Contexts/SiteContext';
import { QrCodeScanner, VIBRATE_DURATION } from '../QrCodeScanner';

function AdHocJobDialog({ show, closeDialog, jobs, setJobs, valves, loadingValves }) {

    AdHocJobDialog.propTypes = {
        show: PropTypes.bool,
        closeDialog: PropTypes.func,
        jobs: PropTypes.array,
        setJobs: PropTypes.func,
        valves: PropTypes.array,
        loadingValves: PropTypes.bool,
        selectedSite: PropTypes.number
    };

    const { selectedSite } = useContext(SiteContext);
    const [job, setJob] = useState({});
    const { valveStates } = useValveStates();

    const [qrScanResult, setQrScanResult] = useState();
    const [previousQrScanResult, setPreviousQrScanResult] = useState();
    const [foundKnownValve, setFoundKnownValve] = useState();
    const [qrScanMessage, setQrScanMessage] = useState();

    const [noQrScanReason, setNoQrScanReason] = useState({});

    const [formValidated, setFormValidated] = useState(false);
    const [createButtonDisabled, setCreateButtonDisabled] = useState(true);
    const { createAdHocJob, loading: loadingCreateAdHocJob } = useCreateAdHocJob();

    useEffect(() => {
        if (selectedSite) {
            setJob({ ...jobs, siteId: selectedSite });
        }
    }, [jobs, selectedSite]);

    const guidEquals = (guid, scannedGuid) => {
        return guid?.toLowerCase() === scannedGuid?.toLowerCase();
    };

    useEffect(() => {
        if (qrScanResult !== undefined && qrScanResult !== previousQrScanResult) {

            const canVibrate = window.navigator.vibrate;
            if (canVibrate) {
                navigator.vibrate(VIBRATE_DURATION);
            }

            console.debug("New QR code scanned", qrScanResult);
            var scannedValve = valves.find((valve) => guidEquals(valve.guid, qrScanResult));

            // Scan was successful if we scanned a known valve
            setFoundKnownValve(scannedValve !== undefined);
            setPreviousQrScanResult(qrScanResult);
            setNoQrScanReason({ selectedReason: "", otherReason: "" });

            if (scannedValve) {
                setQrScanMessage(`Valid QR Code for valve ${scannedValve.tag}`);
                setJob({ ...job, valveId: scannedValve.tag });
                setCreateButtonDisabled(false);
            }
            else {
                setQrScanMessage("Invalid QR Code. Does not match a known valve");
                setCreateButtonDisabled(true);
            }
        }
    }, [job, previousQrScanResult, qrScanResult, valves]);

    const handleQrResult = (result) => {
        if (result !== undefined) {
            setQrScanResult(result[0].rawValue);
        }
    };

    // Disable next button if no reason is selected and valveId has not been set
    const validateForm = (reason, newValveId) => {
        if (reason.selectedReason === "" || (reason.selectedReason === "Other" && reason.otherReason === "")) {
            setCreateButtonDisabled(true);
        } else if (newValveId === "") {
            setCreateButtonDisabled(true);
        } else {
            setCreateButtonDisabled(false);
        }
    };

    // A reason for not scanning is selected
    const handleNoScanReason = (reason) => {
        // Reason has been selected so reset the qr scan results
        setQrScanResult(undefined);
        setFoundKnownValve(undefined);
        setQrScanMessage(undefined);

        // Set the no scan reason in the job
        setJob({ ...job, noQrScanReason: reason.selectedReason === "Other" ? reason.otherReason : reason.selectedReason });
        setNoQrScanReason(reason);

        validateForm(reason, job.valveId);
    };

    const handleValveIdInput = (valveTag) => {
        // Valve Id has been typed so reset the qr scan results
        setQrScanResult(undefined);
        setFoundKnownValve(undefined);
        setQrScanMessage(undefined);

        setJob({ ...job, valveId: valveTag });

        validateForm(noQrScanReason, valveTag);
    };

    // Handle submission of the change state form
    const handleSubmit = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        setFormValidated(true);

        const form = event.currentTarget;

        if (form.checkValidity() === true) {
            job.valveStateId = event.target.newValveState.value;
            await createAdHocJobAndClose();
        }
    };

    const createAdHocJobAndClose = async () => {
        var createdJob = await createAdHocJob(job);

        if (createdJob !== undefined) {
            setJobs([...jobs, createdJob]);
        }
        // reset job and dialog page
        setQrScanResult(undefined);
        setFoundKnownValve(undefined);
        setQrScanMessage(undefined);
        setNoQrScanReason({});
        setFormValidated(false);

        while (loadingCreateAdHocJob) {

        }

        closeDialog();
    };

    return (
        <Modal centered show={show} onHide={closeDialog} modal-dialog-scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Ad hoc Change</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={formValidated} onSubmit={handleSubmit}>
                <Modal.Body >
                    {loadingCreateAdHocJob &&
                        <div className="d-flex justify-content-center mb-2">
                            <Spinner animation="border" />
                        </div>
                    }
                    {!loadingCreateAdHocJob &&
                        <>
                            <p>This will create a record of the movement and send to L3 OMD</p>
                            <QrCodeScanner onScan={handleQrResult} classNames={{ container: "scanner", video: "scanner" }} />
                            {qrScanMessage &&
                                <Alert variant={foundKnownValve ? "success" : "danger"} className="mt-2">{qrScanMessage}</Alert>
                            }
                            <NoQrScanReason reason={noQrScanReason} handleCallback={handleNoScanReason} />
                            {noQrScanReason.selectedReason && noQrScanReason.selectedReason !== "" &&
                                <Form.Group className="mb-3" controlId="valveId">
                                    <ValveIdInput valves={valves} loadingValves={loadingValves} handleCallback={handleValveIdInput} />
                                </Form.Group>
                            }
                            {((noQrScanReason.selectedReason && noQrScanReason.selectedReason !== "" && job.valveId) || (qrScanResult && job.valveId)) &&
                                <Form.Group>
                                    <Form.Select name="newValveState" required>
                                        <option key="0" value="">Set state...</option>
                                        {
                                            valveStates && valveStates.map((s, i) => {
                                                return <option key={i + 1} value={s.id}>{s.name}</option>
                                            })
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        Set the state to complete the job
                                    </Form.Control.Feedback>
                                </Form.Group>
                            }
                        </>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" type="submit" disabled={createButtonDisabled || loadingCreateAdHocJob}>Create</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default AdHocJobDialog;